"use client";
import { useCallback, useEffect, useRef, useState } from "react";

type Opts = {
  onTap: () => void;
  onSeek: (deltaSec: number) => void;
  setRate: (r: number) => void;
  boostRate?: number;
  holdMs?: number;
  doubleTapMs?: number;
  onActivity?: () => void;
};

const MOVE_THRESHOLD = 12;
const LOCK_SWIPE_PX = 44;

export function useVideoGestures(opts: Opts) {
  const boost = opts.boostRate ?? 2;
  const holdMs = opts.holdMs ?? 700;
  const doubleMs = opts.doubleTapMs ?? 280;

  const [holding, setHolding] = useState(false);
  const [locked, setLocked] = useState(false);

  const mode = useRef<"idle" | "hold" | "moved">("idle");
  const start = useRef<{ x: number; y: number } | null>(null);
  const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTap = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockedRef = useRef(false);

  const clearLong = () => {
    if (longTimer.current) {
      clearTimeout(longTimer.current);
      longTimer.current = null;
    }
  };

  const clearPending = () => {
    if (pendingTap.current) {
      clearTimeout(pendingTap.current);
      pendingTap.current = null;
    }
  };

  useEffect(() => () => {
    clearLong();
    clearPending();
  }, []);

  const beginHold = useCallback(() => {
    mode.current = "hold";
    setHolding(true);
    opts.setRate(boost);
    opts.onActivity?.();
  }, [boost, opts]);

  const resolveSingle = useCallback(() => {
    pendingTap.current = null;
    if (lockedRef.current) {
      lockedRef.current = false;
      setLocked(false);
      opts.setRate(1);
    } else {
      opts.onTap();
    }
    opts.onActivity?.();
  }, [opts]);

  const handleTap = useCallback(
    (e: React.PointerEvent) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const rel = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
      const zone = rel < 0.35 ? "left" : rel > 0.65 ? "right" : "center";

      if (pendingTap.current) {
        clearPending();
        if (zone === "left") opts.onSeek(-10);
        else if (zone === "right") opts.onSeek(10);
        else resolveSingle();
        if (zone !== "center") opts.onActivity?.();
        return;
      }

      pendingTap.current = setTimeout(resolveSingle, doubleMs);
    },
    [opts, resolveSingle, doubleMs]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      start.current = { x: e.clientX, y: e.clientY };
      mode.current = "idle";
      clearLong();
      longTimer.current = setTimeout(beginHold, holdMs);
    },
    [beginHold, holdMs]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const s = start.current;
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;

      if (mode.current === "idle") {
        if (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD) {
          clearLong();
          mode.current = "moved";
        }
        return;
      }

      if (mode.current === "hold" && dy < -LOCK_SWIPE_PX && !lockedRef.current) {
        lockedRef.current = true;
        setLocked(true);
        opts.onActivity?.();
      }
    },
    [opts]
  );

  const finish = useCallback(
    (e: React.PointerEvent, cancelled: boolean) => {
      clearLong();
      const m = mode.current;
      if (m === "hold") {
        setHolding(false);
        if (!lockedRef.current) opts.setRate(1);
      } else if (m === "idle" && !cancelled) {
        handleTap(e);
      }
      mode.current = "idle";
      start.current = null;
    },
    [handleTap, opts]
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => finish(e, false), [finish]);
  const onPointerCancel = useCallback((e: React.PointerEvent) => finish(e, true), [finish]);

  return {
    holding,
    locked,
    boost,
    bind: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
      style: {
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      } as React.CSSProperties,
    },
  };
}
